import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';

export type TrialConcept = { conceptId: string; kind: string };
export type TrialInfo = { limited: boolean; concepts: TrialConcept[] };

const listeners = new Set<(t: TrialInfo | null) => void>();
let current: TrialInfo | null = null;

function publish(t: TrialInfo | null) {
  current = t;
  listeners.forEach((l) => l(t));
}

export async function refreshTrial() {
  try {
    publish(await api<TrialInfo>('/trial', { token: true }));
  } catch {
    publish(null);
  }
}

export async function setInTrial(conceptId: string, kind: string, included: boolean) {
  publish(await api<TrialInfo>(`/trial/concepts/${encodeURIComponent(conceptId)}`, { method: 'PUT', token: true, body: { kind, included } }));
}

export function useTrial(): { trial: TrialInfo | null; inTrial: (conceptId: string) => boolean; kindAllowed: (kind: string) => boolean } {
  const { token } = useAuth();
  const [trial, setTrial] = useState<TrialInfo | null>(current);
  useEffect(() => {
    listeners.add(setTrial);
    if (token && current === null) void refreshTrial();
    return () => { listeners.delete(setTrial); };
  }, [token]);
  const inTrial = useCallback((conceptId: string) => !!trial?.concepts.some((c) => c.conceptId === conceptId), [trial]);
  const kindAllowed = useCallback((kind: string) => !trial?.limited || trial.concepts.some((c) => c.kind === kind), [trial]);
  return { trial, inTrial, kindAllowed };
}
